'use client'

import { useEffect } from 'react'
import {
  isOptInRequired,
  hasGpcSignal,
  categoriesToConsentPatch,
} from '../utils/consentMode'

// Cookie-consent CMP.
//
// Wires vanilla-cookieconsent (Orest Bida, MIT, v3.x) up to Google Consent
// Mode v2 with region-adaptive behavior:
//
//   Opt-in required (EEA-27 + UK + IS/LI/NO + CH + BR + Quebec):
//     Modal appears on first visit blocking above-the-fold interaction, with
//     equal-prominence Accept all / Reject all / Manage preferences buttons.
//     Consent stays denied (per app/layout.js default snippet) until the
//     visitor chooses.
//
//   Everywhere else (US non-GPC, Canada non-QC, AU, ROW):
//     Non-blocking inline notice. Analytics + marketing are granted by default
//     (implicit — no region override in the default snippet), so we still fire
//     an explicit consent update to make the granted state visible to GTM.
//     The visitor can revisit their choice via the footer trigger below.
//
//   GPC signal (any region, universally honored):
//     Auto-set ad_storage / ad_user_data / ad_personalization to denied and
//     analytics_storage to granted, then dismiss the banner without asking.
//     GPC is a valid do-not-sell/share opt-out in 12 US states (Sephora paid
//     $1.2M for ignoring this signal, 2022 CA AG settlement).
//
// The CMP calls gtag('consent', 'update', ...) via categoriesToConsentPatch on
// every user choice, unblocking any tags in GTM that gated on consent.

// vanilla-cookieconsent stores its state under this cookie name. Re-consent
// (Quebec Law 25: max 6 months) is enforced by shortening its expires window.
const CONSENT_COOKIE_NAME = 'vrs_cc_cookie'
const CONSENT_COOKIE_MAX_AGE_DAYS = 180

const fireConsentUpdate = (categories) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return
  window.gtag('consent', 'update', categoriesToConsentPatch(categories))
}

// GPC path: silently record consent (analytics granted, marketing denied) and
// dismiss the banner. Runs BEFORE the modal is shown, so GPC visitors never
// see a banner they cannot legally consent through.
const applyGpcConsent = () => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return
  window.gtag('consent', 'update', categoriesToConsentPatch(['analytics']))
}

export default function CookieConsentBanner() {
  useEffect(() => {
    let cancelled = false

    ;(async () => {
      const CookieConsent = await import('vanilla-cookieconsent')
      await import('vanilla-cookieconsent/dist/cookieconsent.css')
      if (cancelled) return

      const optIn = isOptInRequired()
      const gpc = hasGpcSignal()

      // If GPC is on, record consent immediately without a modal. We still
      // initialize vanilla-cookieconsent so the "Cookie preferences" footer
      // link works, but with autoShow disabled and pre-accepted state written.
      if (gpc) {
        applyGpcConsent()
      }

      CookieConsent.run({
        // Opt-in regions: block interaction until choice. Elsewhere:
        // non-blocking inline notice + granted-by-default. GPC: no banner
        // (autoShow off; visitor can still open preferences from the footer).
        autoShow: !gpc,
        disablePageInteraction: optIn && !gpc,
        // Re-consent every 180 days. Quebec Law 25 caps at ~6 months and it
        // makes sense as a global default too.
        cookie: {
          name: CONSENT_COOKIE_NAME,
          expiresAfterDays: CONSENT_COOKIE_MAX_AGE_DAYS,
        },
        guiOptions: {
          consentModal: {
            layout: optIn ? 'box' : 'cloud inline',
            position: optIn ? 'middle center' : 'bottom right',
            equalWeightButtons: true,
            flipButtons: false,
          },
          preferencesModal: {
            layout: 'box',
            position: 'right',
            equalWeightButtons: true,
          },
        },
        categories: {
          necessary: { enabled: true, readOnly: true },
          analytics: { enabled: !optIn },
          marketing: { enabled: !optIn && !gpc },
        },
        onFirstConsent: ({ cookie }) => fireConsentUpdate(cookie.categories || []),
        onConsent: ({ cookie }) => fireConsentUpdate(cookie.categories || []),
        onChange: ({ cookie }) => fireConsentUpdate(cookie.categories || []),
        language: {
          default: 'en',
          translations: {
            en: {
              consentModal: {
                title: 'Cookies on vrs.racing',
                description: optIn
                  ? 'We use cookies to run the site and, with your permission, to measure how it performs and to show relevant ads on Google and Meta. You can accept, reject, or choose which categories to allow.'
                  : 'We use cookies to run the site, measure how it performs, and show relevant ads. You can review or change your choices at any time from "Cookie preferences" in the footer.',
                acceptAllBtn: 'Accept all',
                acceptNecessaryBtn: 'Reject all',
                showPreferencesBtn: 'Manage preferences',
              },
              preferencesModal: {
                title: 'Cookie preferences',
                acceptAllBtn: 'Accept all',
                acceptNecessaryBtn: 'Reject all',
                savePreferencesBtn: 'Save preferences',
                closeIconLabel: 'Close',
                sections: [
                  {
                    title: 'Strictly necessary',
                    description: 'Required for core site features such as your session, cart, and region selection. These cannot be turned off.',
                    linkedCategory: 'necessary',
                  },
                  {
                    title: 'Analytics',
                    description: 'Google Analytics 4 and Microsoft Clarity, used to understand how the site is used so we can improve it. No advertising is done with this data.',
                    linkedCategory: 'analytics',
                  },
                  {
                    title: 'Marketing',
                    description: 'Google Ads and Meta Pixel, used to measure the effectiveness of our ads and to show relevant ads to visitors who have shown interest in our products.',
                    linkedCategory: 'marketing',
                  },
                ],
              },
            },
          },
        },
      })

      // Historically we fired an explicit granted consent update here for
      // non-opt-in regions so GTM Preview showed "granted" instead of "unset"
      // on landing. Removed 2026-08-06: isOptInRequired() falls back to
      // navigator.language when the shippingCountry cookie is not yet set, so
      // an EU visitor with an en-US browser was flagged as non-opt-in and
      // this fire updated their consent to granted BEFORE the CMP even
      // rendered — Jahangir flagged this as a spec violation.
      //
      // The correct behavior is to leave the region-scoped default snippet
      // (denied for EEA/UK/CH/BR, unset elsewhere) as the initial state and
      // let the CMP fire the update only in response to a user click. Google
      // and Meta both treat the "unset" default state as OK to fire tags in
      // non-EEA regions — the cosmetic "unset" in GTM Preview is fine.
    })().catch(() => {
      // A broken CMP must never break the page. Failing silently keeps the
      // denied defaults in effect, which is the compliance-safe fallback.
    })

    return () => {
      cancelled = true
    }
  }, [])

  return null
}
